import { motion } from "framer-motion";
import { ArrowRight, Flame } from "lucide-react";
import { FAVORITE_COOKS } from "@/data/content";
import { track } from "@/lib/analytics";

const EASE = [0.16, 1, 0.3, 1];

export default function FavoriteCooks() {
  return (
    <section
      id="favorites"
      data-testid="favorite-cooks-section"
      className="relative overflow-hidden border-t border-amber-500/10 px-6 pb-8 pt-16 sm:px-10 lg:px-16 lg:pb-10 lg:pt-20"
    >
      <div aria-hidden="true" className="pointer-events-none absolute -left-40 top-0 h-[420px] w-[420px] rounded-full bg-ember/10 blur-[140px]" />
      <div aria-hidden="true" className="pointer-events-none absolute -right-40 bottom-0 h-[420px] w-[420px] rounded-full bg-gold/10 blur-[140px]" />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: EASE }}
        className="relative mb-12 max-w-2xl"
      >
        <p className="mb-3 font-mono text-xs uppercase tracking-[0.35em] text-gold">
          {FAVORITE_COOKS.eyebrow}
        </p>
        <h2 className="bg-gradient-to-r from-amber-100 via-gold to-ember bg-clip-text font-serif text-2xl uppercase tracking-tight text-transparent sm:text-3xl lg:text-4xl">
          {FAVORITE_COOKS.heading}
        </h2>
        <p className="mt-5 text-base font-light leading-relaxed text-neutral-300 sm:text-lg">
          {FAVORITE_COOKS.intro}
        </p>
      </motion.div>

      <div className="relative grid gap-4 lg:grid-cols-3">
        {FAVORITE_COOKS.groups.map((group, groupIndex) => (
          <motion.div
            key={group.title}
            data-testid={`favorite-cooks-group-${groupIndex + 1}`}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: groupIndex * 0.1, ease: EASE }}
            className="border border-amber-500/15 bg-[#141210]/90 p-6 transition-all duration-500 hover:border-ember/50 hover:shadow-[0_0_40px_rgba(230,81,0,0.15)] sm:p-8"
          >
            <div className="mb-6 flex items-center gap-3">
              <Flame className="size-4 text-ember" strokeWidth={1.5} />
              <h3 className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">
                {group.title}
              </h3>
            </div>
            <ul>
              {group.items.map((item, itemIndex) => (
                <li
                  key={item.name}
                  data-testid={`favorite-cook-${groupIndex + 1}-${itemIndex + 1}`}
                  className="border-b border-neutral-800/70 py-5 first:pt-0 last:border-b-0 last:pb-0"
                >
                  <h4 className="font-serif text-lg leading-snug text-amber-100 sm:text-xl">
                    {item.name}
                  </h4>
                  {item.description && (
                    <p className="mt-2 text-sm font-light leading-relaxed text-neutral-400">
                      {item.description}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </motion.div>
        ))}
      </div>

      <motion.a
        href="#contact"
        data-testid="favorite-cooks-contact-cta"
        onClick={() => track("cta_click", { location: "favorite_cooks" })}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.7, delay: 0.2, ease: EASE }}
        className="group relative mt-10 inline-flex items-center gap-3 border border-amber-500/30 px-7 py-4 font-mono text-xs uppercase tracking-[0.25em] text-amber-200 transition-all duration-300 hover:border-amber-400 hover:text-amber-50 hover:shadow-[0_0_40px_rgba(230,81,0,0.2)]"
      >
        Build Your Menu
        <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
      </motion.a>
    </section>
  );
}
